import type { Achievement } from '@/types/achievements';

export const achievementDefinitions: Achievement[] = [
  // ==== HELIPAD ACHIEVEMENTS - "Permission to Fly Free" ====
  {
    id: 'permission-granted',
    name: 'Permission Granted',
    description: 'Achieve parasympathetic nervous system activation in digital helipad',
    tier: 'Bronze',
    category: 'Personal Development',
    requirements: {
      parasympathetic_activation_count: 1,
      biometric_data_points: 5
    },
    points: 100,
    evidence_required: ['432Hz audio response', 'visual safety indicators', 'biometric data'],
    sensory_validation: {
      visual: true,
      audio: true,
      biometric: true,
      emotional_state: true
    },
    unlock_message: 'You have successfully created internal permission to transform. The helicopter awaits.',
    created_at: new Date(),
    updated_at: new Date()
  },
  
  {
    id: 'trust-builder',
    name: 'Trust Builder',
    description: 'Overcome initial resistance and establish platform safety',
    tier: 'Silver',
    category: 'Emotional Intelligence',
    requirements: {
      engagement_score_minimum: 75,
      days_active: 3
    },
    points: 200,
    evidence_required: ['reduced cortisol indicators', 'increased engagement time'],
    unlock_message: 'You have moved from "I don\'t trust anyone" to openness. Trust is rebuilding.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'agency-awakener',
    name: 'Agency Awakener',
    description: 'First recognition of personal choice and control',
    tier: 'Gold',
    category: 'Personal Development',
    requirements: {
      step1_completed: true,
      emotional_state_mastery: true
    },
    points: 300,
    evidence_required: ['Selection of "I want to fly free" with conviction'],
    unlock_message: 'You have reclaimed your sense of personal agency and possibility. From helpless to empowered.',
    created_at: new Date(),
    updated_at: new Date()
  },

  // ==== POST OPT-IN ACHIEVEMENTS - "Curious → Want to Believe" ====
  {
    id: 'curiosity-catalyst',
    name: 'Curiosity Catalyst',
    description: 'Demonstrate genuine curiosity about transformation possibility',
    tier: 'Bronze',
    category: 'Personal Development',
    requirements: {
      engagement_score_minimum: 60,
      days_active: 2
    },
    points: 150,
    evidence_required: ['Engagement with "Could it be?" content', 'time spent exploring'],
    unlock_message: 'You have moved from jadedness to genuine curiosity. Small smile, whimsy detected.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'truth-seeker',
    name: 'Truth Seeker',
    description: 'Commit to truth-telling and authentic self-examination',
    tier: 'Silver',
    category: 'Personal Development',
    requirements: {
      vulnerability_demonstration: true,
      step2_completed: true
    },
    points: 250,
    evidence_required: ['Selection of "I want the truth" with emotional readiness'],
    unlock_message: 'You chose truth over comfort, discomfort over delusion. Courage recognized.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'belief-breakthrough',
    name: 'Belief Breakthrough',
    description: 'Achieve "want to believe" emotional state',
    tier: 'Gold',
    category: 'Emotional Intelligence',
    requirements: {
      parasympathetic_activation_count: 3,
      emotional_state_mastery: true
    },
    points: 350,
    evidence_required: ['Parasympathetic maintenance', 'excitement indicators'],
    unlock_message: 'You have navigated from cynicism to hope. Trust is rebuilding.',
    created_at: new Date(),
    updated_at: new Date()
  },

  // ==== PRE-FLIGHT ACHIEVEMENTS - "I AM the Helicopter" ====
  {
    id: 'identity-integration',
    name: 'Identity Integration',
    description: 'Achieve "I AM the helicopter" recognition',
    tier: 'Bronze',
    category: 'Personal Development',
    requirements: {
      step3_completed: true,
      wisdom_synthesis_score: 70
    },
    points: 200,
    evidence_required: ['Comprehension of Life-Money-Joy-Goodwill framework'],
    unlock_message: 'You recognize yourself as the vehicle of transformation. The metaphor becomes reality.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'pilot-license',
    name: 'Cleared for Flight',
    description: 'Complete pre-flight registration with authentic information',
    tier: 'Silver',
    category: 'Personal Development',
    requirements: {
      vulnerability_demonstration: true,
      story_completion_count: 1
    },
    points: 300,
    evidence_required: ['Honest self-disclosure', 'commitment indicators'],
    unlock_message: 'You are cleared for flight. Begin your helicopter construction journey.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'flight-ready',
    name: 'Flight Ready',
    description: 'Demonstrate readiness for main journey with excitement',
    tier: 'Gold',
    category: 'Personal Development',
    requirements: {
      emotional_state_mastery: true,
      engagement_score_minimum: 85
    },
    points: 400,
    evidence_required: ['Optimal arousal state', 'clear intention setting'],
    unlock_message: 'You have achieved optimal state for transformation work. Energy and commitment confirmed.',
    created_at: new Date(),
    updated_at: new Date()
  },

  // ==== STORY ARCHITECTURE ACHIEVEMENTS ====
  {
    id: 'child-wisdom-recoverer',
    name: 'Child Wisdom Recoverer',
    description: 'Upload childhood photo and recover sense of original agency',
    tier: 'Bronze',
    category: 'Story Architecture',
    requirements: {
      photo_upload_count: 1,
      story_completion_count: 1
    },
    points: 200,
    evidence_required: ['Photo upload', 'narrative connecting to current adult agency'],
    sensory_validation: {
      visual: true,
      emotional_state: true
    },
    unlock_message: 'You have reconnected with your original fearless self. Childhood wisdom recovered.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'hero-journey-architect',
    name: 'Hero\'s Journey Architect',
    description: 'Complete 4 structured stories using Hero\'s Journey framework',
    tier: 'Silver',
    category: 'Story Architecture',
    requirements: {
      story_completion_count: 4,
      wisdom_synthesis_score: 75
    },
    points: 400,
    evidence_required: ['Personal story', 'work story', 'story of receiving help', 'work transformation'],
    unlock_message: 'You have mastered narrative structure for personal transformation. Story elements complete.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'multi-modal-storyteller',
    name: 'Multi-Modal Storyteller',
    description: 'Integrate written, verbal, visual, and sensory elements',
    tier: 'Gold',
    category: 'Multi-Sensory Mastery',
    requirements: {
      story_completion_count: 4,
      voice_recording_count: 2,
      photo_upload_count: 3,
      sensory_anchor_count: 5,
      multi_modal_integration: true
    },
    points: 500,
    evidence_required: ['Stories with photos', 'voice recordings', 'sensory anchors'],
    sensory_validation: {
      visual: true,
      audio: true,
      emotional_state: true
    },
    unlock_message: 'You have created rich, multi-dimensional personal narratives. Modality integration mastered.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'truth-teller',
    name: 'Truth Teller',
    description: 'Demonstrate vulnerability and authenticity in story sharing',
    tier: 'Platinum',
    category: 'Emotional Intelligence',
    requirements: {
      vulnerability_demonstration: true,
      story_completion_count: 4,
      wisdom_synthesis_score: 85
    },
    points: 750,
    evidence_required: ['Emotional honesty', 'recognition of personal patterns'],
    unlock_message: 'You have courageously shared your authentic personal journey. Truth recognized.',
    created_at: new Date(),
    updated_at: new Date()
  },

  // ==== SENSORY ENGAGEMENT ACHIEVEMENTS ====
  {
    id: 'visual-storyteller',
    name: 'Visual Storyteller',
    description: 'Effectively use images to enhance narrative impact',
    tier: 'Bronze',
    category: 'Multi-Sensory Mastery',
    requirements: {
      photo_upload_count: 3,
      story_completion_count: 2
    },
    points: 150,
    evidence_required: ['Meaningful photo selection', 'visual story progression'],
    sensory_validation: {
      visual: true
    },
    unlock_message: 'You have mastered visual elements of personal storytelling. Past → present → future.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'audio-narrator',
    name: 'Audio Narrator',
    description: 'Use voice recording to add emotional depth to stories',
    tier: 'Silver',
    category: 'Multi-Sensory Mastery',
    requirements: {
      voice_recording_count: 3,
      emotional_state_mastery: true
    },
    points: 250,
    evidence_required: ['Voice recordings with authentic emotional expression'],
    sensory_validation: {
      audio: true,
      emotional_state: true
    },
    unlock_message: 'You have added vocal authenticity to your personal narrative. Emotional truth heard.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'sensory-anchor-creator',
    name: 'Sensory Anchor Creator',
    description: 'Identify songs, smells, objects that trigger positive states',
    tier: 'Gold',
    category: 'Multi-Sensory Mastery',
    requirements: {
      sensory_anchor_count: 8,
      emotional_state_mastery: true
    },
    points: 400,
    evidence_required: ['Detailed sensory association mapping'],
    unlock_message: 'You have built a toolkit of sensory triggers for desired emotional states.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'multi-modal-master',
    name: 'Multi-Modal Master',
    description: 'Seamlessly integrate all sensory modalities in storytelling',
    tier: 'Platinum',
    category: 'Multi-Sensory Mastery',
    requirements: {
      multi_modal_integration: true,
      sensory_anchor_count: 10,
      voice_recording_count: 5,
      photo_upload_count: 8,
      story_completion_count: 6
    },
    points: 1000,
    evidence_required: ['Rich, multi-dimensional stories engaging multiple senses'],
    sensory_validation: {
      visual: true,
      audio: true,
      emotional_state: true
    },
    unlock_message: 'You have created immersive, transformative narrative experiences. Global accessibility achieved.',
    created_at: new Date(),
    updated_at: new Date()
  },

  // ==== EMOTIONAL STATE MASTERY ACHIEVEMENTS ====
  {
    id: 'parasympathetic-activator',
    name: 'Parasympathetic Activator',
    description: 'Consistently achieve calm alertness during platform use',
    tier: 'Bronze',
    category: 'Emotional Intelligence',
    requirements: {
      parasympathetic_activation_count: 5,
      biometric_data_points: 15
    },
    points: 200,
    evidence_required: ['Biometric data showing sustained parasympathetic activation'],
    sensory_validation: {
      biometric: true,
      audio: true
    },
    unlock_message: 'You have mastered self-regulation for optimal learning state. 432Hz mastery confirmed.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'state-shifter',
    name: 'State Shifter',
    description: 'Move between emotional states with intention and control',
    tier: 'Silver',
    category: 'Emotional Intelligence',
    requirements: {
      emotional_state_mastery: true,
      vulnerability_demonstration: true
    },
    points: 300,
    evidence_required: ['Demonstrated ability to process difficult emotions safely'],
    unlock_message: 'You have developed emotional agility and resilience. Safe processing mastered.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'joy-schema-activator',
    name: 'Joy Schema Activator',
    description: 'Access intrinsic motivation and flow states',
    tier: 'Gold',
    category: 'Emotional Intelligence',
    requirements: {
      engagement_score_minimum: 90,
      days_active: 14,
      streak_days: 7
    },
    points: 500,
    evidence_required: ['Sustained engagement', 'reported enjoyment', 'creative expression'],
    unlock_message: 'You have reconnected with natural joy and curiosity. Intrinsic motivation activated.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'emotional-alchemist',
    name: 'Emotional Alchemist',
    description: 'Transform difficult emotions into wisdom and strength',
    tier: 'Platinum',
    category: 'Emotional Intelligence',
    requirements: {
      wisdom_synthesis_score: 90,
      vulnerability_demonstration: true,
      story_completion_count: 6
    },
    points: 750,
    evidence_required: ['Stories showing transformation of pain into growth'],
    unlock_message: 'You have mastered emotional transformation as leadership skill. Pain → wisdom → service.',
    created_at: new Date(),
    updated_at: new Date()
  },

  // ==== ALERISLIFE ENTERPRISE ACHIEVEMENTS ====
  {
    id: 'welcome-red-helicopter',
    name: 'Welcome to Red Helicopter',
    description: 'Complete platform introduction and overview',
    tier: 'Bronze',
    category: 'Getting Started',
    requirements: {
      step1_completed: true
    },
    points: 100,
    aleris_specific: true,
    unlock_message: 'Welcome to your leadership transformation journey. The helicopter is ready.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'senior-living-advocate',
    name: 'Senior Living Advocate',
    description: 'Complete all steps with focus on senior living excellence',
    tier: 'Silver',
    category: 'Senior Living Excellence',
    requirements: {
      step1_completed: true,
      step2_completed: true,
      step3_completed: true,
      engagement_score_minimum: 80
    },
    points: 400,
    aleris_specific: true,
    unlock_message: 'You have demonstrated commitment to senior living excellence.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'care-leadership-master',
    name: 'Care Leadership Master',
    description: 'Excel in all simulation scenarios with values-centered choices',
    tier: 'Gold',
    category: 'Care Leadership',
    requirements: {
      simulation_mastery: true,
      values_centered_percentage: 85,
      leadership_impact_score: 80
    },
    points: 600,
    aleris_specific: true,
    unlock_message: 'You have mastered values-centered leadership in senior living contexts.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'senior-care-business-philosopher',
    name: 'Senior Care Business Philosopher',
    description: 'Master all simulation scenarios with values-centered choices',
    tier: 'Platinum',
    category: 'Senior Living Excellence',
    requirements: {
      simulation_mastery: true,
      values_centered_percentage: 90,
      step6_completed: true,
      wisdom_synthesis_score: 85,
      care_quality_improvement: 20
    },
    points: 1000,
    aleris_specific: true,
    unlock_message: 'You have achieved mastery in values-centered senior living leadership. Philosophy and practice united.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'team-catalyst',
    name: 'Team Catalyst',
    description: 'Help colleagues through mentoring and support',
    tier: 'Silver',
    category: 'Team Collaboration',
    requirements: {
      team_mentoring_sessions: 3,
      leadership_impact_score: 70
    },
    points: 350,
    aleris_specific: true,
    unlock_message: 'You have catalyzed transformation in others. Leadership through service.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'community-transformer',
    name: 'Community Transformer',
    description: 'Demonstrate measurable impact on care quality and team performance',
    tier: 'Platinum',
    category: 'Care Leadership',
    requirements: {
      care_quality_improvement: 25,
      leadership_impact_score: 90,
      team_mentoring_sessions: 8
    },
    points: 1200,
    aleris_specific: true,
    unlock_message: 'You have transformed your community through exceptional leadership. Care quality elevated.',
    created_at: new Date(),
    updated_at: new Date()
  },

  // ==== HIDDEN/SURPRISE ACHIEVEMENTS ====
  {
    id: 'night-owl-transformer',
    name: 'Night Owl Transformer',
    description: 'Complete significant progress during late night hours',
    tier: 'Bronze',
    category: 'Personal Development',
    requirements: {
      step2_completed: true
    },
    points: 150,
    hidden: true,
    unlock_message: 'Even in the quiet hours, transformation continues. Dedication recognized.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'perfectionist-overcomer',
    name: 'Perfectionist Overcomer',
    description: 'Submit authentic, imperfect content showing vulnerability',
    tier: 'Silver',
    category: 'Personal Development',
    requirements: {
      vulnerability_demonstration: true,
      story_completion_count: 2
    },
    points: 300,
    hidden: true,
    unlock_message: 'You chose authenticity over perfection. Courage in imperfection recognized.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'consistency-champion',
    name: 'Consistency Champion',
    description: 'Maintain daily engagement for 30 consecutive days',
    tier: 'Gold',
    category: 'Personal Development',
    requirements: {
      streak_days: 30,
      days_active: 30
    },
    points: 500,
    hidden: true,
    unlock_message: 'Consistency creates transformation. Your dedication has built unstoppable momentum.',
    created_at: new Date(),
    updated_at: new Date()
  },

  // ==== POST-FLIGHT DYNAMIC COMMUNITY ECOSYSTEM ENGAGEMENT ====
  {
    id: 'community-ecosystem-pioneer',
    name: 'Community Ecosystem Pioneer',
    description: 'Enter the Post-Flight Dynamic Community Ecosystem with auto-generated flight reports, equity-funded projects, and positive reverberation tracking',
    tier: 'Platinum',
    category: 'Community Development',
    requirements: {
      step6_completed: true,
      all_components_unlocked: true,
      flight_reports_submitted: 1,
      community_projects_participated: 1,
      ripple_effects_tracked: 3
    },
    points: 1000,
    evidence_required: [
      'Monthly flight report with LIFE-MONEY-JOY balance metrics',
      'Community project application based on LIFE values and MONEY capability',
      'Positive reverberation tracking (hobbies impact, relationship quality, community contribution)',
      'Qualitative storytelling + quantitative metrics documentation'
    ],
    sensory_validation: {
      visual: true,
      emotional_state: true,
      community_feedback: true
    },
    unlock_message: 'You have entered the Post-Flight Dynamic Community Ecosystem. Your red helicopter journey continues through ongoing growth, community building, and positive reverberation creation.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'monthly-flight-reporter',
    name: 'Monthly Flight Reporter',
    description: 'Submit consistent monthly flight reports tracking LIFE-MONEY-JOY integration',
    tier: 'Silver',
    category: 'Community Development',
    requirements: {
      flight_reports_submitted: 3,
      life_balance_consistency: 75,
      money_progress_consistency: 75,
      joy_level_consistency: 75
    },
    points: 300,
    evidence_required: [
      'Auto-generated reports based on completion patterns',
      'Relationship updates documentation',
      'Purpose evolution tracking',
      'Monthly goals with accountability metrics'
    ],
    unlock_message: 'Your consistent flight reporting demonstrates ongoing commitment to integration and growth.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'equity-funded-collaborator',
    name: 'Equity-Funded Collaborator',
    description: 'Participate in invitation-based, equity-funded community projects',
    tier: 'Gold',
    category: 'Community Development',
    requirements: {
      community_projects_participated: 2,
      values_alignment_score: 85,
      money_capability_demonstration: true,
      collaborative_leadership: true
    },
    points: 500,
    evidence_required: [
      'Selection based on LIFE values and MONEY capability',
      'Values-aligned leadership demonstration',
      'Financial literacy workshop participation',
      'Purpose discovery mentorship engagement'
    ],
    unlock_message: 'You have proven your ability to collaborate in values-driven, equity-funded community initiatives.',
    created_at: new Date(),
    updated_at: new Date()
  },

  {
    id: 'positive-reverberation-tracker',
    name: 'Positive Reverberation Tracker',
    description: 'Master qualitative storytelling + quantitative metrics for ripple effect documentation',
    tier: 'Diamond',
    category: 'Impact Measurement',
    requirements: {
      ripple_effects_tracked: 10,
      hobbies_impact_documented: 3,
      relationship_quality_improvements: 5,
      community_contributions_measured: 2,
      qualitative_stories_rich: true,
      quantitative_metrics_comprehensive: true
    },
    points: 750,
    evidence_required: [
      'Hobbies impact documentation with people impacted count',
      'Relationship quality improvements with time invested metrics',
      'Community contribution stories with value created measurements',
      'Comprehensive qualitative narratives',
      'Quantitative metrics (people impacted, time invested, value created)'
    ],
    sensory_validation: {
      community_feedback: true,
      impact_verification: true,
      story_authenticity: true
    },
    unlock_message: 'You have mastered the art of tracking and documenting positive reverberations. Your impact ripples far beyond immediate reach.',
    created_at: new Date(),
    updated_at: new Date()
  }
];

export const getAchievementById = (id: string): Achievement | undefined => {
  return achievementDefinitions.find(achievement => achievement.id === id);
};

export const getAchievementsByCategory = (category: string): Achievement[] => {
  return achievementDefinitions.filter(achievement => achievement.category === category);
};

export const getAchievementsByTier = (tier: string): Achievement[] => {
  return achievementDefinitions.filter(achievement => achievement.tier === tier);
};

export const getAlerisSpecificAchievements = (): Achievement[] => {
  return achievementDefinitions.filter(achievement => achievement.aleris_specific);
};

export const getVisibleAchievements = (): Achievement[] => {
  return achievementDefinitions.filter(achievement => !achievement.hidden);
};

export const getHiddenAchievements = (): Achievement[] => {
  return achievementDefinitions.filter(achievement => achievement.hidden);
};